import { isGlobalViewer } from '../../../constants/roles';
// src/modules/treasury/pages/InterBranchTransfer.jsx
// Branch-facing Inter-Branch Cash Transfer (IBCT).
// A branch sends physical cash (denomination-counted) to another branch / HO.
// In-transit model: sender is debited on send; receiver is credited only on
// confirmation. Both balances feed each branch's EOD automatically.
//
//   Outgoing tab → transfers this branch SENT  (Cancel while In Transit)
//   Incoming tab → transfers this branch will RECEIVE (Confirm / Reject)

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Card, Table, Typography, Spin, Alert, Row, Col, Button, Tag, Tabs, Modal,
  Select, Input, Tooltip, Badge, message, Space, Descriptions, Popconfirm,
} from 'antd';
import {
  SwapOutlined, SendOutlined, ReloadOutlined, CheckOutlined, CloseOutlined,
  InboxOutlined, ExportOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchById, fetchBranches } from '../../branches/api';
import {
  fetchTransfers, createTransfer, confirmTransfer, rejectTransfer, cancelTransfer,
} from '../api';
import DenominationCountForm from '../components/DenominationCountForm';
import {
  DENOMINATION_NOTES, INTER_BRANCH_TRANSFER_STATUS as ST,
  INTER_BRANCH_TRANSFER_STATUS_OPTIONS,
} from '../constants/treasuryConstants';

const { Title, Text } = Typography;
const { TextArea } = Input;

const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');

const statusTag = (status) => {
  const o = INTER_BRANCH_TRANSFER_STATUS_OPTIONS.find((x) => x.value === status);
  return <Tag color={o?.color || 'default'}>{o?.label || status}</Tag>;
};

const denomTotal = (counts) =>
  DENOMINATION_NOTES.reduce((s, n) => s + (counts[n.key] || 0) * n.value, 0);

// Compact "₹500 × 4 · ₹100 × 10" denomination summary
const DenomBreakdown = ({ denominations = {} }) => {
  const parts = DENOMINATION_NOTES.filter((n) => denominations[n.key] > 0);
  if (!parts.length) return <Text type="secondary">—</Text>;
  return (
    <Space size={[8, 4]} wrap>
      {parts.map((n) => (
        <Tag key={n.key} style={{ margin: 0 }}>
          {n.label} × {denominations[n.key]}
        </Tag>
      ))}
    </Space>
  );
};

export default function InterBranchTransfer() {
  const { user } = useAuth();
  const [initLoading, setInitLoading] = useState(true);
  const [isHO, setIsHO] = useState(false);
  const [myBranch, setMyBranch] = useState(null);
  const [branches, setBranches] = useState([]);

  const [outgoing, setOutgoing] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('outgoing');

  // Send modal
  const [sendOpen, setSendOpen] = useState(false);
  const [toBranchId, setToBranchId] = useState(null);
  const [counts, setCounts] = useState({});
  const [narration, setNarration] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reject / cancel reason modal
  const [reasonModal, setReasonModal] = useState({ open: false, type: null, row: null });
  const [reason, setReason] = useState('');
  const [acting, setActing] = useState(false);

  const myBranchId = myBranch?._id;

  // ── Init: resolve own branch + branch list ──
  useEffect(() => {
    (async () => {
      try {
        if (!user?.branch) { setInitLoading(false); return; }
        const branchRes = await fetchBranchById(user.branch);
        const branch = branchRes?.data || branchRes;
        setMyBranch(branch);
        setIsHO(branch?.branchType === 'HO' || isGlobalViewer(user));

        const brRes = await fetchBranches({ limit: 1000 });
        setBranches(brRes?.data?.data || brRes?.data || []);
      } catch {
        setError('Failed to load profile');
      } finally {
        setInitLoading(false);
      }
    })();
  }, [user]);

  const load = useCallback(async () => {
    if (!myBranchId) return;
    setLoading(true);
    setError(null);
    try {
      const [outRes, inRes] = await Promise.all([
        fetchTransfers({ branchId: myBranchId, direction: 'outgoing', limit: 200 }),
        fetchTransfers({ branchId: myBranchId, direction: 'incoming', limit: 200 }),
      ]);
      setOutgoing(outRes?.data?.data || []);
      setIncoming(inRes?.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load transfers');
    } finally {
      setLoading(false);
    }
  }, [myBranchId]);

  useEffect(() => { if (!initLoading) load(); }, [initLoading, load]);

  const sendTotal = useMemo(() => denomTotal(counts), [counts]);
  const destOptions = useMemo(
    () => branches.filter((b) => b._id !== myBranchId),
    [branches, myBranchId],
  );

  const resetSend = () => { setToBranchId(null); setCounts({}); setNarration(''); };

  const handleSend = async () => {
    if (!toBranchId) return message.warning('Select a destination branch');
    if (sendTotal <= 0) return message.warning('Enter the cash denomination breakdown');
    setSubmitting(true);
    try {
      await createTransfer({ toBranchId, denominations: counts, amount: sendTotal, narration: narration || undefined });
      message.success(`₹${fmt(sendTotal)} sent — awaiting receiver confirmation`);
      setSendOpen(false); resetSend(); load();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to send cash');
    } finally {
      setSubmitting(false);
    }
  };

  const doConfirm = async (row) => {
    setActing(true);
    try {
      await confirmTransfer(row._id);
      message.success(`Confirmed receipt of ₹${fmt(row.amount)}`);
      load();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to confirm');
    } finally {
      setActing(false);
    }
  };

  const openReason = (type, row) => { setReason(''); setReasonModal({ open: true, type, row }); };

  const submitReason = async () => {
    const { type, row } = reasonModal;
    setActing(true);
    try {
      if (type === 'reject') await rejectTransfer(row._id, { reason });
      else await cancelTransfer(row._id, { reason });
      message.success(type === 'reject' ? 'Transfer rejected and reversed to sender' : 'Transfer cancelled and reversed');
      setReasonModal({ open: false, type: null, row: null });
      load();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Action failed');
    } finally {
      setActing(false);
    }
  };

  const baseColumns = [
    { title: 'Date', dataIndex: 'createdAt', width: 110, render: (v) => (v ? dayjs(v).format('DD/MM/YYYY') : '—') },
    { title: 'Reference', dataIndex: 'reference', width: 130, render: (v) => <Text code style={{ fontSize: 12 }}>{v}</Text> },
    { title: 'Amount', dataIndex: 'amount', width: 130, align: 'right', render: (v) => <Text strong>₹{fmt(v)}</Text> },
    { title: 'Status', dataIndex: 'status', width: 120, render: statusTag },
  ];

  const outgoingColumns = [
    baseColumns[0],
    { title: 'To', dataIndex: ['toBranch', 'name'], width: 180, render: (_, r) => <span>{r.toBranch?.name} <Text type="secondary">({r.toBranch?.code})</Text></span> },
    baseColumns[1], baseColumns[2], baseColumns[3],
    {
      title: 'Action', key: 'action', width: 120, fixed: 'right',
      render: (_, row) => (row.status === ST.PENDING ? (
        <Button size="small" danger icon={<CloseOutlined />} onClick={() => openReason('cancel', row)}>Cancel</Button>
      ) : <Text type="secondary">—</Text>),
    },
  ];

  const incomingColumns = [
    baseColumns[0],
    { title: 'From', dataIndex: ['fromBranch', 'name'], width: 180, render: (_, r) => <span>{r.fromBranch?.name} <Text type="secondary">({r.fromBranch?.code})</Text></span> },
    baseColumns[1], baseColumns[2], baseColumns[3],
    {
      title: 'Action', key: 'action', width: 180, fixed: 'right',
      render: (_, row) => (row.status === ST.PENDING ? (
        <Space>
          <Popconfirm title="Confirm cash received?" description={`₹${fmt(row.amount)} will be added to your till.`} okText="Confirm" onConfirm={() => doConfirm(row)}>
            <Button size="small" type="primary" icon={<CheckOutlined />}>Confirm</Button>
          </Popconfirm>
          <Button size="small" danger icon={<CloseOutlined />} onClick={() => openReason('reject', row)}>Reject</Button>
        </Space>
      ) : <Text type="secondary">—</Text>),
    },
  ];

  const expandable = {
    expandedRowRender: (row) => (
      <Descriptions size="small" column={1} bordered style={{ maxWidth: 720 }}>
        <Descriptions.Item label="Denominations"><DenomBreakdown denominations={row.denominations} /></Descriptions.Item>
        {row.narration && <Descriptions.Item label="Narration">{row.narration}</Descriptions.Item>}
        <Descriptions.Item label="Initiated By">{row.initiatedBy?.name || '—'}</Descriptions.Item>
        {row.receivedBy && <Descriptions.Item label="Received By">{row.receivedBy?.name} · {dayjs(row.receivedAt).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>}
        {row.rejectedBy && <Descriptions.Item label="Rejected">{row.rejectedBy?.name}{row.rejectionReason ? ` — ${row.rejectionReason}` : ''}</Descriptions.Item>}
        {row.cancelledBy && <Descriptions.Item label="Cancelled">{row.cancelledBy?.name}{row.cancellationReason ? ` — ${row.cancellationReason}` : ''}</Descriptions.Item>}
      </Descriptions>
    ),
  };

  const incomingPending = incoming.filter((t) => t.status === ST.PENDING).length;

  if (initLoading) return <Spin style={{ display: 'block', marginTop: 80 }} />;

  return (
    <div style={{ padding: '16px 0' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}>
            <SwapOutlined style={{ marginRight: 8 }} />
            Inter-Branch Cash Transfer
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {myBranch?.name ? `${myBranch.name} (${myBranch.code})` : 'Send and receive physical cash between branches'}
          </Text>
        </Col>
        <Col>
          <Space>
            <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={load} loading={loading} /></Tooltip>
            <Button type="primary" icon={<SendOutlined />} onClick={() => setSendOpen(true)}>Send Cash</Button>
          </Space>
        </Col>
      </Row>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'outgoing',
            label: <span><ExportOutlined /> Outgoing ({outgoing.length})</span>,
            children: (
              <Table
                dataSource={outgoing} columns={outgoingColumns} rowKey="_id" loading={loading}
                size="small" scroll={{ x: 900 }} expandable={expandable}
                pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} sent` }}
                locale={{ emptyText: 'No cash sent yet' }}
              />
            ),
          },
          {
            key: 'incoming',
            label: <span><InboxOutlined /> Incoming <Badge count={incomingPending} offset={[4, -2]} /></span>,
            children: (
              <Table
                dataSource={incoming} columns={incomingColumns} rowKey="_id" loading={loading}
                size="small" scroll={{ x: 980 }} expandable={expandable}
                pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} received` }}
                locale={{ emptyText: 'No incoming transfers' }}
              />
            ),
          },
        ]}
      />

      {/* ── Send modal ── */}
      <Modal
        title={<span><SendOutlined /> Send Cash to Another Branch</span>}
        open={sendOpen}
        onCancel={() => { setSendOpen(false); resetSend(); }}
        onOk={handleSend}
        okText={`Send ₹${fmt(sendTotal)}`}
        okButtonProps={{ disabled: !toBranchId || sendTotal <= 0, loading: submitting }}
        width={560}
        destroyOnClose
      >
        <div style={{ marginBottom: 12 }}>
          <Text strong>Destination</Text>
          <Select
            placeholder="Select branch / HO" showSearch optionFilterProp="children"
            style={{ width: '100%', marginTop: 4 }} value={toBranchId} onChange={setToBranchId}
          >
            {destOptions.map((b) => (
              <Select.Option key={b._id} value={b._id}>{b.name} ({b.code}){b.branchType === 'HO' ? ' — HO' : ''}</Select.Option>
            ))}
          </Select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Text strong>Cash Denomination</Text>
          <div style={{ marginTop: 4 }}>
            <DenominationCountForm counts={counts} onChange={setCounts} />
          </div>
        </div>

        <div style={{ marginBottom: 4 }}>
          <Text strong>Narration (optional)</Text>
          <TextArea rows={2} value={narration} onChange={(e) => setNarration(e.target.value)} placeholder="Reason / remarks for this transfer" style={{ marginTop: 4 }} />
        </div>

        <Alert
          type="info" showIcon style={{ marginTop: 8 }}
          message={`₹${fmt(sendTotal)} will leave your till immediately and stay in transit until the receiver confirms.`}
        />
      </Modal>

      {/* ── Reason modal (reject / cancel) ── */}
      <Modal
        title={reasonModal.type === 'reject' ? 'Reject Transfer' : 'Cancel Transfer'}
        open={reasonModal.open}
        onCancel={() => setReasonModal({ open: false, type: null, row: null })}
        onOk={submitReason}
        okText={reasonModal.type === 'reject' ? 'Reject & Reverse' : 'Cancel & Reverse'}
        okButtonProps={{ danger: true, loading: acting }}
        destroyOnClose
      >
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message={`₹${fmt(reasonModal.row?.amount)} will be reversed back to ${reasonModal.row?.fromBranch?.name || 'the sender'}.`}
        />
        <Text>Reason (optional)</Text>
        <TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} style={{ marginTop: 4 }} />
      </Modal>
    </div>
  );
}
